import { JSX, ReactNode, useCallback, useRef, useState } from "react";
import { Session } from "sip.js/lib/api/session";
import { SessionManager, SessionManagerOptions } from "sip.js/lib/platform/web";
import { ProviderContext } from "./SIPProviderContext";
import {
  RegisterStatus,
  SIPAccount,
  SIPProviderOptions,
  CONNECT_STATUS,
  SessionTimer,
  ErrorMessageLevel1,
  ErrorMessageLevel2,
} from "./type";
import { ExtendedSessionManager } from "./ExtendedSessionManager";
import { isPlatform } from '@ionic/react';
import { isAppRunningOnMobile } from "../shared/utils/platform";

/**
 * SIPProvider wraps your application, handles connecting to the Asterisk server,
 * registering the SIP user, and passing down the session manager/context.
 */
export const SIPProvider = (props: {
  options: SIPProviderOptions;
  children: ReactNode | JSX.Element;
  mergedSessionManagerOptions?: SessionManagerOptions;
}): JSX.Element => {
  const { options, mergedSessionManagerOptions = {}, children } = props;

  // Refs to remote audio/video tags for media playback
  const refAudioRemote = useRef<HTMLAudioElement>(null);
  const refVideoRemote = useRef<HTMLVideoElement>(null);

  // State for active sessions and session timers
  const [sessions, setSessions] = useState<Record<string, Session>>({});
  const [sessionTimer, setSessionTimer] = useState<SessionTimer>({});

  // The SessionManager instance from sip.js
  const [sessionManager, setSessionManager] = useState<SessionManager | null>(
    null
  );
  const [isCallAnswered, setIsCallAnswered] = useState<boolean>(false);

  // Connection & registration statuses
  const [connectStatus, setConnectStatus] = useState<CONNECT_STATUS>(
    CONNECT_STATUS.WAIT_REQUEST_CONNECT
  );
  const [registerStatus, setRegisterStatus] = useState<RegisterStatus>(
    RegisterStatus.UNREGISTERED
  );

  /**
   * Update local state for a session
   */
  const updateSession = useCallback((session: Session) => {
    setSessions((prevSessions) => ({
      ...prevSessions,
      [session.id]: session,
    }));
  }, []);

  /**
   * Initiates the SIP connection and registers the user with Asterisk
   */
  const connectAndRegister = useCallback(async (sipAccount: SIPAccount) => {
    // if (sessionManager && connectStatus === CONNECT_STATUS.CONNECTED) {
    //   // Close the current connection gracefully
    //   // If SIP.js provides a stop or disconnect method:
    //   sessionManager.unregister(); // Dispose current sessionManager if supported
    //   // Alternatively:
    //   sessionManager.userAgent?.stop(); // if such API exists
    //   setSessionManager(null);
    //   setConnectStatus(CONNECT_STATUS.DISCONNECTED);
    // }
    setIsCallAnswered(false);
    let publicIP = "";
    // await fetch("https://api.ipify.org?format=json")
    //   .then((response) => response.json())
    //   .then((data) => (publicIP = data.ip));
    // console.log("::debug publicIP:", publicIP);

    await fetch("https://checkip.amazonaws.com")
      .then((response) => response.text())
      .then((ip) => {
        publicIP = ip.trim();
      })
      .catch((error) => {
        console.error("Error fetching IP:", error);
      });
    console.log("::debug publicIP:", publicIP);

    // Build our SessionManager from sip.js
    const manager = new ExtendedSessionManager(options.webSocketServer, {
      aor: `sip:${sipAccount.username}@${options.domain}`,
      userAgentOptions: {
        authorizationUsername: sipAccount.username,
        authorizationPassword: sipAccount.password,
        viaHost: publicIP,
      },
      media: {
        constraints: {
          audio: true,
          video: false,
        },
        remote: {
          audio:
            options.refAudioRemote ??
            (refAudioRemote.current as HTMLAudioElement),
          video:
            options.refVideoRemote ??
            (refVideoRemote.current as HTMLVideoElement),
        },
      },

      delegate: {
        onCallCreated: (session) => {
          // Watch for state changes on the session
          session.stateChange.addListener((state) => {
            console.log("::debug SIPProvider call onCallCreated:", session);
            console.info(
              ErrorMessageLevel1.SIP_PROVIDER,
              `Session ${session.id} changed to ${state}`
            );
            updateSession(session);
          });
          // Store session locally
          updateSession(session);

          // Track a creation timestamp
          setSessionTimer((timer: any) => ({
            ...timer,
            [session.id]: {
              createdAt: new Date(),
            },
          }));
        },
        onCallAnswered: (session) => {
          updateSession(session);
          setSessionTimer((timer: any) => ({
            ...timer,
            [session.id]: {
              ...(timer[session.id] || {}),
              answeredAt: new Date(),
            },
          }));
          setIsCallAnswered(true);

          const isMobile = isAppRunningOnMobile();
          if (isMobile && refAudioRemote.current) {
            const audioElement = refAudioRemote.current;
            if (typeof audioElement.setSinkId === "function") {
              audioElement
                .setSinkId("default")
                .then(() => console.log("Audio routed to earpiece"))
                .catch((err) => console.error("Error setting sinkId:", err));
            }
          }
        },
        onCallHangup: (session) => {
          updateSession(session);
          setSessionTimer((timer: any) => ({
            ...timer,
            [session.id]: {
              ...(timer[session.id] || {}),
              hangupAt: new Date(),
            },
          }));
          setIsCallAnswered(false);
        },
        onCallReceived: (session) => {
          // Inbound call
          console.log("::onCallReceived:");
          updateSession(session);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setSessionTimer((timer: any) => ({
            ...timer,
            [session.id]: {
              ...(timer[session.id] || {}),
              receivedAt: new Date(),
            },
          }));
        },
        onRegistered: () => {
          setRegisterStatus(RegisterStatus.REGISTERED);
        },
        onUnregistered: () => {
          setRegisterStatus(RegisterStatus.UNREGISTERED);
        },
        onServerConnect() {
          setConnectStatus(CONNECT_STATUS.CONNECTED);
          manager.register();
        },
        onServerDisconnect(error) {
          console.error(
            ErrorMessageLevel1.SIP_PROVIDER,
            ErrorMessageLevel2.FAILED_CONNECT_SIP_USER,
            error
          );
          setConnectStatus(CONNECT_STATUS.DISCONNECTED);
        },
      },
      ...mergedSessionManagerOptions,
    });

    setSessionManager(manager);
    manager.connect();
  }, []);

  return (
    <>
      <ProviderContext.Provider
        value={{
          connectAndRegister,
          sessionManager,
          connectStatus,
          registerStatus,
          sessions,
          sessionTimer,
          isCallAnswered,
        }}
      >
        {children}
      </ProviderContext.Provider>
      {/* Render hidden audio/video elements to handle remote media */}
      <audio ref={refAudioRemote} id="remoteAudio" playsInline />
      <video ref={refVideoRemote} />
    </>
  );
};
